import { useEffect, useMemo, useState } from 'react';
import './styles.css'; // Phase 4.1 patch4 — was orphaned after merge; load review stylesheet.
import { useQuery, useQueryClient } from './query.js';
import {
  api,
  type ClusterSummary,
  type Concept,
  type PortalGroup,
  type PortalDetail,
  type Template,
} from './api.js';
import { ConceptEditor } from './ConceptEditor.js';
import { QuestionEditor } from './QuestionEditor.js';
import { NewPortalQuestionModal } from './NewPortalQuestionModal.js';
import { PromoteModal } from './PromoteModal.js';
import { LifecycleChip, ReviewVerdictChip, OriginChip } from './widgets.js';

/**
 * Phase 4.1 merge — ReviewApp is mounted by main.tsx ONLY when:
 *   1. window.location.pathname starts with '/review', AND
 *   2. the shared battle session reports role ∈ {'sme','admin'}.
 *
 * Authentication + role-gating happen in the parent (RootRouter in main.tsx),
 * so this component assumes it has a valid SME session and never renders Login.
 *
 * ---------------------------------------------------------------------------
 * M9 — SME Console Clarity
 *
 * The previous console had two reported problems:
 *   1. SMEs could only see DRAFT rows (server filtered `WHERE status='DRAFT'`),
 *      so they had no way to see what was already live or retired.
 *   2. Three same-shaped chips per row (`[GENUINE][DRAFT][pending]`) gave no
 *      axis labels — SMEs couldn't tell which chip meant what.
 *
 * M9 fixes both:
 *   - Server now returns ALL rows; client filters via the toolbar below.
 *   - Chips are now {LifecycleChip,ReviewVerdictChip,OriginChip} with explicit
 *     `Lifecycle: …` / `Review: …` / `Origin: …` labels (see widgets.tsx).
 *
 * Filter state is mirrored to `window.location.hash` so bookmarking a filtered
 * view is possible (e.g. `/review#lifecycle=draft&review=pending`). The DEFAULT
 * filter is `lifecycle=draft` + `review=pending` — i.e. what an SME starts
 * their shift looking at — which matches the pre-M9 behaviour as closely as
 * possible while letting them broaden the view on demand. RETIRED rows are
 * hidden unless explicitly opted in via `Lifecycle: All (incl. retired)`.
 * ---------------------------------------------------------------------------
 */
export interface ReviewAppProps {
  /** Display name of the signed-in SME (from /api/session.student.name). */
  reviewerName: string;
  /** Called when the user clicks “Sign out” — should hit /api/logout + reload. */
  onLogout: () => Promise<void> | void;
  /** Called when the user clicks “Play game” — navigates to '/'. */
  onExitToGame: () => void;
}

const TIER_LABEL: Record<string, string> = {
  path6: 'Path',
  conditional: 'Conditional',
  final: 'Final',
};

// ---- Filter state machine -------------------------------------------------
//
// `LifecycleFilter` mirrors the four lifecycle scopes an SME might want:
//   draft        — only items still in the editing/review queue (default)
//   active       — only items currently in play (released to students)
//   all_no_ret   — both DRAFT and ACTIVE (everything that isn't retired)
//   all_inc_ret  — everything including RETIRED (audit / archaeology mode)
//
// `ReviewFilter` cross-cuts lifecycle:
//   pending      — needs SME review (default — the SME's working set)
//   approved     — already approved by an SME
//   rejected     — already rejected by an SME
//   all          — no filter on review_status
//
// Both filters apply CLIENT-SIDE to data returned by the server. The server
// no longer gates by status='DRAFT'; it returns everything and we narrow here.

type LifecycleFilter = 'draft' | 'active' | 'all_no_ret' | 'all_inc_ret';
type ReviewFilter = 'pending' | 'approved' | 'rejected' | 'all';

const LIFECYCLE_OPTIONS: { value: LifecycleFilter; label: string }[] = [
  { value: 'draft', label: 'Draft only' },
  { value: 'active', label: 'Active only' },
  { value: 'all_no_ret', label: 'All (no retired)' },
  { value: 'all_inc_ret', label: 'All (incl. retired)' },
];

const REVIEW_OPTIONS: { value: ReviewFilter; label: string }[] = [
  { value: 'pending', label: 'Needs review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All verdicts' },
];

interface Filters {
  lifecycle: LifecycleFilter;
  review: ReviewFilter;
}

function parseHash(): Filters {
  const def: Filters = { lifecycle: 'draft', review: 'pending' };
  try {
    const raw = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#/, '');
    if (!raw) return def;
    const params = new URLSearchParams(raw);
    const lc = params.get('lifecycle');
    const rv = params.get('review');
    return {
      lifecycle:
        lc === 'active' || lc === 'all_no_ret' || lc === 'all_inc_ret' || lc === 'draft'
          ? (lc as LifecycleFilter)
          : def.lifecycle,
      review:
        rv === 'approved' || rv === 'rejected' || rv === 'all' || rv === 'pending'
          ? (rv as ReviewFilter)
          : def.review,
    };
  } catch {
    return def;
  }
}

function writeHash(f: Filters) {
  try {
    const params = new URLSearchParams();
    params.set('lifecycle', f.lifecycle);
    params.set('review', f.review);
    const next = '#' + params.toString();
    if (typeof window !== 'undefined' && window.location.hash !== next) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + next);
    }
  } catch {
    /* hash write is best-effort; never throw into render */
  }
}

/** Pure predicate — does this lifecycle status pass the filter? */
function lifecyclePass(status: string, f: LifecycleFilter): boolean {
  const s = (status || '').toUpperCase();
  switch (f) {
    case 'draft':
      return s === 'DRAFT';
    case 'active':
      return s === 'ACTIVE';
    case 'all_no_ret':
      return s !== 'RETIRED';
    case 'all_inc_ret':
      return true;
  }
}

/** Pure predicate — does this review verdict pass the filter? */
function reviewPass(status: string, f: ReviewFilter): boolean {
  if (f === 'all') return true;
  return (status || '').toLowerCase() === f;
}

/**
 * Per-cluster derived counts using the *currently selected* filters.
 * Total = rows passing both filters. Approved = of those passing the lifecycle
 * filter, how many are approved. Live = ACTIVE+approved. Synth = of those
 * passing both filters, how many are SYNTHETIC. We compute these from the
 * server-side aggregates (no need to fetch every row).
 *
 * The aggregates ClusterSummary carries are:
 *   total/approved/pending/rejected/synth/draft/active/retired (study side)
 *   battle_total/battle_approved/battle_draft/battle_active/battle_retired (battle side)
 *
 * The Rail count line summarises STUDY rows (parallel to pre-M9 behaviour),
 * since the rail row IS a study cluster. We surface `live` as a separate
 * indicator regardless of the current Lifecycle filter so SMEs always know
 * how many study items are released to students.
 */
interface RailCounts {
  visible: number; // total study rows passing both filters
  approved: number; // approved study rows passing lifecycle filter
  live: number; // study rows with status=ACTIVE (always shown, filter-independent)
  synth: number; // SYNTHETIC study rows passing both filters
}

function computeRailCounts(c: ClusterSummary, f: Filters): RailCounts {
  // We approximate "rows passing the filter" using the cluster's coarse counts.
  // For the lifecycle axis, we already have draft/active/retired splits server-side.
  // For the review-verdict axis we have approved/pending/rejected.
  // We cannot multiply them (no cross-tabs on the server), so the rail shows
  // approved-counts within the lifecycle scope and a separate "live" indicator.
  // The actual concept rows in the rail are filtered exactly by the per-row
  // predicates below; the count line is a heads-up summary.
  let visible: number;
  switch (f.lifecycle) {
    case 'draft':
      visible = c.draft ?? 0;
      break;
    case 'active':
      visible = c.active ?? 0;
      break;
    case 'all_no_ret':
      visible = (c.draft ?? 0) + (c.active ?? 0);
      break;
    case 'all_inc_ret':
      visible = c.total ?? 0;
      break;
  }
  const approved =
    f.lifecycle === 'all_inc_ret' || f.lifecycle === 'all_no_ret' || f.lifecycle === 'active'
      ? c.approved ?? 0
      : 0; // DRAFT-scope very rarely contains approved rows; show 0 to avoid lying.
  return {
    visible,
    approved,
    live: c.active ?? 0,
    synth: c.synth ?? 0,
  };
}

// ---------------------------------------------------------------------------

export function ReviewApp({ reviewerName, onLogout, onExitToGame }: ReviewAppProps) {
  const qc = useQueryClient();
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [selectedConcept, setSelectedConcept] = useState<string | null>(null);
  const [selectedPortal, setSelectedPortal] = useState<string | null>(null);
  const [showPromote, setShowPromote] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState<Filters>(() => parseHash());

  // Persist filter changes into the URL hash so SMEs can bookmark a view.
  useEffect(() => {
    writeHash(filters);
  }, [filters]);

  function showToast(msg: string, kind: 'success' | 'error') {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 4000);
  }

  async function logout() {
    qc.clear();
    await onLogout();
  }

  return (
    <div className="app-shell review-root">
      <div className="topbar">
        <h1>EconQuest · SME Review</h1>
        <button className="sm" onClick={() => setShowPromote(true)} data-testid="button-open-promote">
          Promote approved…
        </button>
        <button className="ghost sm" onClick={onExitToGame} data-testid="button-exit-to-game">
          Play game
        </button>
        <div className="spacer" />
        <span className="who" data-testid="text-reviewer">
          {reviewerName}
        </span>
        <button className="ghost sm" onClick={logout} data-testid="button-logout">
          Sign out
        </button>
      </div>

      {/* M9 filter bar — narrow the rail without re-querying the server. */}
      <FilterBar filters={filters} onChange={setFilters} />

      <div className="main-grid">
        <Rail
          filters={filters}
          selectedCluster={selectedCluster}
          selectedConcept={selectedConcept}
          selectedPortal={selectedPortal}
          onSelectCluster={(c) => {
            setSelectedCluster(c);
            setSelectedConcept(null);
            setSelectedPortal(null);
          }}
          onSelectConcept={(code) => {
            setSelectedConcept(code);
            setSelectedPortal(null);
          }}
          onSelectPortal={(p) => {
            setSelectedPortal(p);
            setSelectedCluster(null);
            setSelectedConcept(null);
          }}
        />
        {selectedPortal ? (
          <PortalPane portalKey={selectedPortal} filters={filters} />
        ) : (
          <ConceptPane clusterId={selectedCluster} conceptCode={selectedConcept} />
        )}
      </div>

      {showPromote && (
        <PromoteModal
          clusterId={selectedCluster}
          conceptCode={selectedConcept}
          onClose={() => {
            setShowPromote(false);
            qc.invalidateQueries({ queryKey: ['clusters'] });
            if (selectedCluster) qc.invalidateQueries({ queryKey: ['concepts', selectedCluster] });
          }}
          onToast={showToast}
        />
      )}

      {toast && (
        <div className={`toast ${toast.kind}`} data-testid="toast">
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ---- Filter toolbar -------------------------------------------------------
// Sits between the topbar and the main grid. Two select drop-downs:
// Lifecycle and Review verdict. Stateless; parent owns the filter object.
//
// We keep the toolbar markup intentionally simple — no fancy chip toggles —
// because SMEs use this every shift and unfamiliar UI patterns slow them down.

function FilterBar({ filters, onChange }: { filters: Filters; onChange: (f: Filters) => void }) {
  return (
    <div className="filter-bar" data-testid="filter-bar">
      <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Filters:
      </span>
      <label className="filter-field">
        <span>Lifecycle</span>
        <select
          value={filters.lifecycle}
          onChange={(e) => onChange({ ...filters, lifecycle: e.target.value as LifecycleFilter })}
          data-testid="filter-lifecycle"
        >
          {LIFECYCLE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <label className="filter-field">
        <span>Review</span>
        <select
          value={filters.review}
          onChange={(e) => onChange({ ...filters, review: e.target.value as ReviewFilter })}
          data-testid="filter-review"
        >
          {REVIEW_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }} data-testid="filter-hint">
        Showing {filters.lifecycle === 'all_inc_ret' ? 'all' : 'non-retired'} rows ·{' '}
        {filters.review === 'all' ? 'all verdicts' : `${filters.review} only`}
      </span>
    </div>
  );
}

// ---- left rail: two axes — clusters (study + intra) and portals ----------

function Rail({
  filters,
  selectedCluster,
  selectedConcept,
  selectedPortal,
  onSelectCluster,
  onSelectConcept,
  onSelectPortal,
}: {
  filters: Filters;
  selectedCluster: string | null;
  selectedConcept: string | null;
  selectedPortal: string | null;
  onSelectCluster: (c: string) => void;
  onSelectConcept: (code: string) => void;
  onSelectPortal: (p: string) => void;
}) {
  const clusters = useQuery({
    queryKey: ['clusters'],
    queryFn: () => api.get<ClusterSummary[]>('/api/clusters'),
  });

  const concepts = useQuery({
    queryKey: ['concepts', selectedCluster],
    queryFn: () => api.get<Concept[]>(`/api/concepts?cluster=${selectedCluster}`),
    enabled: Boolean(selectedCluster),
  });

  const portals = useQuery({
    queryKey: ['portals'],
    queryFn: () => api.get<PortalGroup[]>('/api/portals'),
  });

  // Cluster filtering: hide rows whose `visible` count under the current
  // filters drops to zero (a cluster that has zero rows matching the SME's
  // current view is pure noise in the rail).
  const visibleClusters = useMemo(() => {
    return (clusters.data ?? []).filter((c) => computeRailCounts(c, filters).visible > 0);
  }, [clusters.data, filters]);

  // Concept-row filtering: lifecycle scope + review verdict, applied to each
  // concept's study row. RETIRED rows hidden by default per user decision
  // (only the `all_inc_ret` scope shows them).
  const visibleConcepts = useMemo(() => {
    if (!concepts.data) return [];
    return concepts.data.filter(
      (c) => lifecyclePass(c.status, filters.lifecycle) && reviewPass(c.review_status, filters.review),
    );
  }, [concepts.data, filters]);

  // Portal-row filtering: same rule applied using server-side per-status
  // aggregates. We hide a portal whose entire `total` would disappear under
  // the filter — i.e. a portal with zero rows under the lifecycle scope.
  const visiblePortals = useMemo(() => {
    return (portals.data ?? []).filter((p) => {
      let inScope: number;
      switch (filters.lifecycle) {
        case 'draft':
          inScope = p.draft ?? 0;
          break;
        case 'active':
          inScope = p.live ?? 0;
          break;
        case 'all_no_ret':
          inScope = (p.draft ?? 0) + (p.live ?? 0);
          // Fallback for old servers that don't expose draft/live yet.
          if ((p.draft ?? 0) === 0 && (p.live ?? 0) === 0) inScope = p.total;
          break;
        case 'all_inc_ret':
          inScope = p.total;
          break;
      }
      return inScope > 0;
    });
  }, [portals.data, filters]);

  return (
    <div className="rail">
      <div className="rail-section-title">Clusters</div>
      {clusters.isLoading && <div className="spinner">Loading clusters…</div>}
      {clusters.isError && <div className="err" style={{ padding: 12 }}>Failed to load (DB unavailable?)</div>}
      {!clusters.isLoading && visibleClusters.length === 0 && (clusters.data?.length ?? 0) > 0 && (
        <div className="muted" style={{ padding: '6px 12px', fontSize: 11 }} data-testid="empty-clusters-filtered">
          No clusters match the current filters.
        </div>
      )}
      {visibleClusters.map((c) => {
        const rc = computeRailCounts(c, filters);
        return (
          <div key={c.cluster_id}>
            <button
              className={`cluster-row ${selectedCluster === c.cluster_id ? 'active' : ''}`}
              onClick={() => onSelectCluster(c.cluster_id)}
              data-testid={`cluster-${c.cluster_id}`}
            >
              <span className="cid">{c.cluster_id}</span>
              <span className="meta">
                {rc.approved}/{rc.visible} approved · {rc.live} live · {rc.synth} synth
              </span>
            </button>
            {selectedCluster === c.cluster_id && (
              <div>
                {concepts.isLoading && <div className="spinner">Loading…</div>}
                {visibleConcepts.map((concept) => (
                  <button
                    key={concept.id}
                    className={`concept-row ${selectedConcept === concept.concept_code ? 'active' : ''}`}
                    onClick={() => onSelectConcept(concept.concept_code)}
                    data-testid={`concept-${concept.concept_code}`}
                  >
                    <span className="code">{concept.concept_code}</span>
                    <span
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {concept.concept_title}
                    </span>
                    {/* M9 — compact chips in the rail. No axis label (the toolbar above
                        tells SMEs what they're looking at); the rail row would get too
                        wide otherwise. Hover gives the full label via title via
                        widget testid. The full-label chips show up in the editor pane. */}
                    <OriginChip quality={concept.source_quality} size="sm" showLabel={false} />
                    <LifecycleChip status={concept.status} size="sm" showLabel={false} />
                    <ReviewVerdictChip status={concept.review_status} size="sm" showLabel={false} />
                  </button>
                ))}
                {visibleConcepts.length === 0 && (concepts.data?.length ?? 0) === 0 && (
                  <div className="muted" style={{ padding: '6px 22px' }}>No concepts.</div>
                )}
                {visibleConcepts.length === 0 && (concepts.data?.length ?? 0) > 0 && (
                  <div
                    className="muted"
                    style={{ padding: '6px 22px', fontSize: 11 }}
                    data-testid={`empty-concepts-filtered-${c.cluster_id}`}
                  >
                    No concepts match filters. ({concepts.data?.length ?? 0} hidden)
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div className="rail-section-title" style={{ marginTop: 14 }}>Portals</div>
      {portals.isLoading && <div className="spinner">Loading portals…</div>}
      {portals.isError && <div className="err" style={{ padding: 12 }}>Failed to load (DB unavailable?)</div>}
      {visiblePortals.map((p) => (
        <button
          key={p.portal_key}
          className={`portal-row ${selectedPortal === p.portal_key ? 'active' : ''}`}
          onClick={() => onSelectPortal(p.portal_key)}
          data-testid={`portal-${p.portal_key}`}
        >
          <span className="cid" style={{ fontFamily: 'var(--mono)' }}>{p.portal_key.replace('-', ' ↔ ')}</span>
          <span className="meta">
            {p.tier ? `${TIER_LABEL[p.tier] ?? p.tier} · ` : ''}
            {p.marks ? `${p.marks}m · ` : ''}
            {p.approved}/{p.total} approved · {p.live ?? 0} live · {p.synth} synth
          </span>
        </button>
      ))}
      {!portals.isLoading && visiblePortals.length === 0 && (portals.data?.length ?? 0) > 0 && (
        <div className="muted" style={{ padding: '6px 22px', fontSize: 11 }} data-testid="empty-portals-filtered">
          No portals match the current filters.
        </div>
      )}
      {portals.data?.length === 0 && <div className="muted" style={{ padding: '6px 22px' }}>No portals.</div>}
    </div>
  );
}

// ---- main concept pane ----------------------------------------------------

function ConceptPane({ clusterId, conceptCode }: { clusterId: string | null; conceptCode: string | null }) {
  const detail = useQuery({
    queryKey: ['concept', clusterId, conceptCode],
    queryFn: () => api.get<Concept>(`/api/concept/${clusterId}/${conceptCode}`),
    enabled: Boolean(clusterId && conceptCode),
  });

  if (!clusterId) return <div className="detail muted">Select a cluster from the left.</div>;
  if (!conceptCode) return <div className="detail muted">Select a concept to review.</div>;
  if (detail.isLoading) return <div className="detail spinner">Loading concept…</div>;
  if (detail.isError || !detail.data)
    return <div className="detail err">Failed to load concept (DB unavailable?).</div>;

  return <ConceptEditor key={detail.data.id} concept={detail.data} />;
}

// ---- portal pane: inter-cluster 6/9/12-mark questions by portal_key -------

function PortalPane({ portalKey, filters }: { portalKey: string; filters: Filters }) {
  const qc = useQueryClient();
  const [showNewQuestion, setShowNewQuestion] = useState(false);
  const detail = useQuery({
    queryKey: ['portal', portalKey],
    queryFn: () => api.get<PortalDetail>(`/api/portal/${portalKey}`),
    enabled: Boolean(portalKey),
  });

  if (detail.isLoading) return <div className="detail spinner">Loading portal…</div>;
  if (detail.isError || !detail.data)
    return <div className="detail err">Failed to load portal (DB unavailable?).</div>;

  const allTemplates: Template[] = detail.data.templates;
  // Same row-filter as the rail: lifecycle scope + review verdict.
  const templates = allTemplates.filter(
    (t) => lifecyclePass(t.status, filters.lifecycle) && reviewPass(t.review_status, filters.review),
  );
  const hidden = allTemplates.length - templates.length;

  return (
    <div className="detail" data-testid={`portal-detail-${portalKey}`}>
      <div className="toolbar">
        <h2 style={{ margin: 0, fontSize: 16, fontFamily: 'var(--mono)' }}>
          Portal {portalKey.replace('-', ' ↔ ')}
        </h2>
        <span className="muted">
          {templates.length} question{templates.length === 1 ? '' : 's'}
          {hidden > 0 ? ` · ${hidden} hidden by filters` : ''}
        </span>
        <button
          className="primary sm"
          type="button"
          style={{ marginLeft: 'auto' }}
          onClick={() => setShowNewQuestion(true)}
          data-testid="button-suggest-new-portal-question"
        >
          Suggest new question
        </button>
      </div>
      {templates.length === 0 && allTemplates.length === 0 && (
        <p className="muted" style={{ padding: 12 }}>No questions in this portal.</p>
      )}
      {templates.length === 0 && allTemplates.length > 0 && (
        <p
          className="muted"
          style={{ padding: 12 }}
          data-testid={`empty-portal-filtered-${portalKey}`}
        >
          All {allTemplates.length} questions are hidden by the current filters. Widen the toolbar above to see them.
        </p>
      )}
      {templates.map((t) => (
        <QuestionEditor key={t.id} tmpl={t} invalidateKeys={[['portal', portalKey], ['portals']]} />
      ))}
      {showNewQuestion && (
        <NewPortalQuestionModal
          portalKey={portalKey}
          onClose={() => setShowNewQuestion(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['portal', portalKey] });
            qc.invalidateQueries({ queryKey: ['portals'] });
          }}
        />
      )}
    </div>
  );
}
